import React, { useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import PinStrikethroughIcon from '@atlaskit/icon-lab/core/pin-strikethrough';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { PinType } from '../../types';
import { TypeIcon } from '../TypeIcon';
import { UnpinConfirmModal } from '../unpin-confirm-modal/UnpinConfirmModal';

import messages from './messages';

const styles = cssMap({
	container: {
		position: 'relative',
		width: '100%',
		borderRadius: token('radius.large'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		// Actions are hidden until the row is hovered or an action inside it receives
		// keyboard focus — focus-within keeps them reachable for keyboard/AT users who
		// never trigger :hover.
		// @ts-expect-error -- strict cssMap API does not model attribute selectors
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-testid$="-actions-wrapper"]': {
			opacity: 0,
			transition: 'opacity 150ms ease-in-out',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover [data-testid$="-actions-wrapper"], &:focus-within [data-testid$="-actions-wrapper"]': {
			opacity: 1,
		},
	},
	icon: {
		flexShrink: 0,
	},
	title: {
		flex: '1',
		// Prevents a long cached title from being clipped without ellipsis by its flex sibling.
		minWidth: '0',
	},
	// Title fills row until hover, when this overlays its right edge instead of pushing it aside.
	actions: {
		position: 'absolute',
		insetInlineEnd: token('space.100'),
		insetBlockStart: 0,
		insetBlockEnd: 0,
		paddingInlineStart: token('space.150'),
		backgroundColor: token('elevation.surface'),
		backgroundImage: `linear-gradient(${token('color.background.neutral.subtle.hovered')}, ${token('color.background.neutral.subtle.hovered')})`,
		borderRadius: token('radius.medium'),
	},
});

interface PinnedItemRowProps {
	type: PinType;
	title: string;
	/** Navigates to the pinned item. */
	onOpen?: () => void;
	/** Starts a Rovo chat about the pinned item. Presentational-only for now — wired in M5.3 (TREX-1880), blocked on TREX-1782. */
	onStartChat?: () => void;
	/** Removes the item from the pinned list. */
	onUnpin?: () => void;
}

export const PinnedItemRow = ({
	type,
	title,
	onOpen,
	onStartChat,
	onUnpin,
}: PinnedItemRowProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const titleRef = useRef<HTMLElement | null>(null);
	const [isTitleTruncated, setIsTitleTruncated] = useState(false);
	const [isUnpinModalOpen, setIsUnpinModalOpen] = useState(false);

	// Re-checked on hover rather than on mount/resize — the row's width can settle after
	// mount (e.g. panel animating open), so a fresh measurement on hover avoids acting on a
	// stale layout. `Text` truncates via `-webkit-line-clamp` (a vertical box), so overflow
	// shows up as extra scrollHeight against clientHeight, not scrollWidth/clientWidth.
	const checkTitleTruncation = () => {
		const element = titleRef.current;
		if (element) {
			setIsTitleTruncated(element.scrollHeight > element.clientHeight);
		}
	};

	return (
		<Flex
			xcss={styles.container}
			alignItems="center"
			gap="space.150"
			testId="rovo-for-you-pinned-item-row"
		>
			<Box xcss={styles.icon}>
				<TypeIcon type={type} />
			</Box>
			<Tooltip
				content={isTitleTruncated ? title : ''}
				testId="rovo-for-you-pinned-item-row-title-tooltip"
			>
				{(tooltipProps) => (
					<Box
						xcss={styles.title}
						{...tooltipProps}
						onMouseOver={(event: React.MouseEvent<HTMLElement>) => {
							checkTitleTruncation();
							tooltipProps.onMouseOver(event);
						}}
					>
						<Text ref={titleRef} maxLines={1}>
							{title}
						</Text>
					</Box>
				)}
			</Tooltip>
			<Flex
				xcss={styles.actions}
				alignItems="center"
				gap="space.050"
				testId="rovo-for-you-pinned-item-row-actions-wrapper"
			>
				<IconButton
					icon={AiChatIcon}
					label={formatMessage(messages.pinnedItemRowStartChatLabel)}
					appearance="subtle"
					spacing="compact"
					onClick={onStartChat}
					testId="rovo-for-you-pinned-item-row-start-chat-button"
				/>
				<IconButton
					icon={LinkExternalIcon}
					label={formatMessage(messages.pinnedItemRowOpenLabel)}
					appearance="subtle"
					spacing="compact"
					onClick={onOpen}
					testId="rovo-for-you-pinned-item-row-open-button"
				/>
				<IconButton
					icon={PinStrikethroughIcon}
					label={formatMessage(messages.pinnedItemRowUnpinLabel)}
					appearance="subtle"
					spacing="compact"
					onClick={() => setIsUnpinModalOpen(true)}
					testId="rovo-for-you-pinned-item-row-unpin-button"
				/>
			</Flex>
			<UnpinConfirmModal
				isOpen={isUnpinModalOpen}
				onClose={() => setIsUnpinModalOpen(false)}
				onConfirm={() => {
					setIsUnpinModalOpen(false);
					onUnpin?.();
				}}
			/>
		</Flex>
	);
};
